import { skipToken, useQuery } from '@tanstack/react-query';
import { MessageSquareTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/shared/components/date';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { messengerKeys } from '../api';
import type { ChatListItem } from '../types';

/**
 * The name of the chat a message hit belongs to: a group's title (published with the message), "Favourites" for the saved
 * chat, and for a direct chat — whose name is the other person, different for each reader — the person from the chat list
 * when it is loaded (the list is not fetched for this), else a generic label.
 */
function useMessageChatLabel(meta: Record<string, string> | null | undefined): string {
  const { t } = useTranslation('messenger');
  const chatId = meta?.chatId;
  const cached = useQuery<ChatListItem[]>({ queryKey: messengerKeys.chatList(''), queryFn: skipToken });
  const chat = chatId ? cached.data?.find((item) => item.id === chatId) : undefined;
  switch (meta?.chatType) {
    case 'saved':
      return t('saved.title');
    case 'group':
      return chat?.title ?? meta.chatTitle ?? t('search.groupChat');
    default:
      return chat?.peer?.fullName ?? chat?.title ?? t('search.directChat');
  }
}

/**
 * A chat message in the global search results (the content of the search page's link row; meta: chatId, chatType, chatTitle,
 * authorName, seq): where it was written and by whom on the first line, the text with the matches marked below. The row
 * links to the message in its chat.
 */
export function MessageSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const chat = useMessageChatLabel(result.meta);
  const author = result.meta?.authorName;
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-brand-text shadow-xs">
        <MessageSquareTextIcon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden />
      </span>
      <span className="min-w-0 flex-1" data-testid="message-search-result">
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="truncate font-medium text-foreground/70" data-testid="message-search-chat">
            {chat}
          </span>
          {author && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate" data-testid="message-search-author">
                {author}
              </span>
            </>
          )}
          {result.updatedAt && (
            <>
              <span aria-hidden>·</span>
              <RelativeTime date={result.updatedAt} />
            </>
          )}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[0.9375rem] break-words text-foreground">
          {highlights?.title ?? result.title}
        </span>
        {snippet && (
          <span className="mt-0.5 line-clamp-2 block text-sm break-words text-muted-foreground">
            {snippet}
          </span>
        )}
      </span>
    </>
  );
}
